import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';

import { checkDevFlag, filterSearchParams, getSwitchUserTypeUrl } from '/packages/shared-components/utils/util';
import Header, { PartnerInfo } from '/packages/shared-components/Header/Header';
import { ConfirmSwitchModal } from '/packages/shared-components/Header/ConfirmSwitchModal';
import { useModal } from '/packages/shared-components/utils/modal';
import { clearTokens } from '/packages/shared-components/utils/cognito';
import { useUser } from '~/src/utils/user';
import { usePartnerContext } from '~/src/utils/partnerProvider';
import { globalError } from '~src/utils/apollo';

import SWITCH_PARTNER from '~src/queries/codegen/switchPartner';

const HeaderWrapper = () => {
  const { currentUser, permissions } = useUser();
  const { partnerContext, setPartnerContext } = usePartnerContext();
  const { open: modalOpen, setOpen: setModalOpen, setContent, updateModalSettings } = useModal();
  const [open, setOpen] = useState<boolean>(false);
  const [openUser, setOpenUser] = useState<boolean>(false);
  const handleToggle = () => setOpen((prevOpen) => !prevOpen);
  const handleClose = () => setOpen(false);
  const handleUserToggle = () => setOpenUser((prevOpen) => !prevOpen);
  const handleUserClose = () => setOpenUser(false);
  const { handleModalClose } = useModal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const displayPartnerList =
    currentUser?.partnerWorkstreams
      ?.filter((item) => {
        return item.partner.id !== partnerContext.id;
      })
      .slice(0, 3) || [];

  const [switchPartner] = useMutation(SWITCH_PARTNER, {
    onCompleted: (data) => {
      data.switchPartner?.selectedPartner &&
        setPartnerContext({
          id: data.switchPartner?.selectedPartner?.id,
          name: data.switchPartner?.selectedPartner?.name,
        });
    },
  });

  const handlePartnerClick = (id: string) => {
    const list = displayPartnerList.find((partner) => {
      return partner.partnerId === id;
    });
    if (list) {
      const partnerName = list.partner.name;
      if (!modalOpen) {
        updateModalSettings({ maxWidth: 'sm' });
        setModalOpen(true);
        setContent(
          <ConfirmSwitchModal
            partnerName={partnerName || ''}
            id={id}
            handleSwitchPartnerConfirm={handleSwitchPartnerConfirm}
            handleModalClose={handleModalClose}
          />,
        );
      }
    }
  };
  const handleEventChange = () => {
    setOpen(false);
    setOpenUser(false);
  };

  const navigateApp = (pathname: string) => {
    const search = filterSearchParams(searchParams);
    pathname.startsWith('/app') && navigate({ pathname, search });
    !pathname.startsWith('/app') && window.location.assign(`${process.env.REACT_APP_BASE_URI}${pathname}${search}`);
  };

  const changeNavigation = (path: string) => {
    globalError(null);
    return navigateApp(path);
  };

  const handleSwitchPartnerConfirm = async (id: string) => {
    const search = filterSearchParams(searchParams);
    try {
      await switchPartner({
        variables: { partnerId: id },
        onCompleted: () => {
          navigate(`/app/partner/${id}${search}`);
        },
      });
    } finally {
      handleModalClose();
    }
  };

  const switchUserRole = () => {
    const switchTo = permissions.isPartner ? 'Disney' : 'Partner';
    fetch(getSwitchUserTypeUrl(switchTo)).then(() => window.location.reload());
  };

  const logout = () => {
    clearTokens();
    navigate('/app/loggedOut');
  };

  const accepted = currentUser?.termsOfService?.accepted;

  const partnerInfo: PartnerInfo = {
    id: partnerContext.id,
    name: partnerContext.name,
  };

  return (
    <Header
      checkDevFlag={checkDevFlag()}
      partnerInfo={partnerInfo}
      termsAccepted={accepted}
      workstreams={permissions.workstreams}
      userType={currentUser?.userType}
      isPortalAdmin={permissions.admin}
      partnerWorkstreams={displayPartnerList}
      partnerWorkstreamsLength={currentUser && currentUser?.partnerWorkstreams.length}
      handlePartnerClick={handlePartnerClick}
      handleClose={handleClose}
      handleToggle={handleToggle}
      handleUserClose={handleUserClose}
      handleUserToggle={handleUserToggle}
      handleEventChange={handleEventChange}
      open={open}
      openUser={openUser}
      changeNavigation={changeNavigation}
      switchRole={switchUserRole}
      logout={logout}
      isAccessManagementAdmin={permissions.accessManagement.admin}
    />
  );
};

export default HeaderWrapper;
